import React, { useContext } from 'react';
import { CenteredContentLayout } from '../../../components/containers/CenteredContentLayout';
import { Panel } from '../../../components/containers/Panel';
import classes from './MobileApp.module.scss';
import classNames from 'classnames';
import appleStoreBadge from '../../../assets/images/appleStoreBadge.svg';
import goolgePlayBadge from '../../../assets/images/goolgePlayBadge.svg';
import { Badge } from '../../../components/containers/Badge';
import { BadgeColorsEnum, BadgeSizesEnum } from '../../../constants/badge';
import { format } from 'date-fns';
import { DateFormatSetting } from '../../../models/graphql';
import { useSettings } from '../../../hooks/useSettings';
import { Table } from '../../../components/containers/Table';
import { useSelectedOrganization } from '../../../hooks/useSelectedOrganization';
import { BeaconContext } from '../../../providers/BeaconProvider';
import { Tooltip } from '../../../components/actions/Tooltip';
import { PanelHeader } from '../../../components/containers/Panel/PanelHeader.view';
import { Debugger } from '../../../components/containers/Debugger';

const getFormattedDate = (dateTimeString: string, dateFormat: DateFormatSetting | undefined) => {
  const date = new Date(dateTimeString);
  const formatString = dateFormat === DateFormatSetting.DAY_MONTH_YEAR ? 'dd MMM yyyy' : 'MMM dd, yyyy';

  return format(date, formatString);
};

const platform = (type: string | undefined) => {
  switch (type) {
    case 'AndroidApp':
      return 'Android';
    case 'IosApp':
      return 'iOS';
    default:
      return '--';
  }
};
export const MobileAppView = (): JSX.Element => {
  const { selectedOrganization } = useSelectedOrganization();
  const { selectedOrgSettings } = useSettings();

  const mobileApps = selectedOrganization?.mobileApps || [];
  const { beacon } = useContext(BeaconContext);
  const badgeContainerClasses = classNames('padding-top-small-8 padding-bottom-small-12', classes.BadgeContainer);
  const orgIsFromLegacy = selectedOrganization?.fromLegacy;

  return (
    <CenteredContentLayout headerTitle={'SmarterQueue Mobile App'} loading={false} hasSidebar={true}>
      <Panel>
        <div className={classes.MobileApp}>
          <PanelHeader
            title={'SmarterQueue Mobile App'}
            subTitle={
              <div>
                Share content on the go, review your latest analytics, and publish Instagram posts more easily.{' '}
                <a onClick={() => beacon('article', '741', { type: 'modal' })}>Learn more</a>
              </div>
            }
          />
          {mobileApps.length > 0 && (
            <>
              Review which of your devices have our app installed and connected successfully:
              <div className={'padding-top-small-8 padding-bottom-small-8'}>
                <Table tableStyle={'stacked_scrolling'}>
                  <>
                    <thead>
                      <tr>
                        <th>Platform</th>
                        <th>Device Name</th>
                        <th>Date Installed</th>
                        <th>Status</th>
                        {!orgIsFromLegacy && <th>Owned By</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {mobileApps.map(app => (
                        <tr key={app.id}>
                          <td>
                            <div className={'relative'}>
                              <Debugger tooltipContent={'Mobile App ID: ' + app.id} modalContent={app} />
                            </div>
                            {platform(app.__typename)}
                          </td>
                          <td>{app.deviceName}</td>
                          <td>{getFormattedDate(app.firstConnectedTime, selectedOrgSettings?.dateFormat)}</td>
                          <td>
                            <div className={classes.StatusBadgeWrapper}>
                              {app.connected ? (
                                <Badge
                                  color={BadgeColorsEnum.GREEN15}
                                  size={BadgeSizesEnum.MEDIUM}
                                  text={'Connected'}
                                />
                              ) : (
                                <div id={`disconected${app.id}`} className={classes.DisconnectedBadgeWrapper}>
                                  <Badge
                                    color={BadgeColorsEnum.RED}
                                    size={BadgeSizesEnum.MEDIUM}
                                    text={'Disconnected'}
                                  />
                                  <Tooltip autoHide={false} target={`#disconected${app.id}`} type={'regular'}>
                                    <div>
                                      <p className={'small-text'}>
                                        The mobile app has not been connected fully to your account, or it has lost its
                                        connection.
                                      </p>
                                      <p className={'small-text'}>
                                        Follow our{' '}
                                        <a onClick={() => beacon('article', '741', { type: 'modal' })}>
                                          tips for how to fix this issue
                                        </a>
                                        , then reload this page to verify the connection again.
                                      </p>
                                    </div>
                                  </Tooltip>
                                </div>
                              )}
                            </div>
                          </td>
                          {!orgIsFromLegacy && <td>{app.owner.firstName}</td>}
                        </tr>
                      ))}
                    </tbody>
                  </>
                </Table>
              </div>
            </>
          )}
          <ol>
            <li className={'padding-top-small-8 padding-bottom-small-8'}>
              <div>Download the SmarterQueue mobile app.</div>

              <div className={badgeContainerClasses}>
                <img
                  src={appleStoreBadge}
                  alt={'Apple Store Badge'}
                  onClick={() => window.open('https://apps.apple.com/gb/app/smarterqueue/id1106644941')}
                />
                <img
                  src={goolgePlayBadge}
                  alt={'Goolge Play Badge'}
                  onClick={() =>
                    window.open(
                      'https://play.google.com/store/apps/details?id=com.smarterqueue.android&pcampaignid=sq_website_tools&pli=1'
                    )
                  }
                />
              </div>
            </li>
            <li className={'padding-top-small-12 padding-bottom-small-12'}>
              <div className={classes.BulletPointLabel}>Log into the mobile app</div>
              <div className={classes.BulletPointContent}>Using your SmarterQueue email and password.</div>
            </li>
            <li className={'padding-top-small-12 padding-bottom-small-12'}>
              <div className={classes.BulletPointLabel}>Enable notifications</div>
              <div className={classes.BulletPointContent}>
                Swipe through to the end of the tutorial slides, and make sure that you enable all permissions and
                notifications on your mobile device.
              </div>
            </li>
          </ol>
        </div>
      </Panel>
    </CenteredContentLayout>
  );
};
